import RefreshIcon from '@mui/icons-material/Refresh'
import SearchIcon from '@mui/icons-material/Search'
import MenuIcon from '@mui/icons-material/Menu'
import AddIcon from '@mui/icons-material/Add'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import NewBookingHeaderButton from 'src/clinic/components/ClinicManageBookingsPage/headerComponents/NewBookingHeaderButton'
import BookingDateNavigator from 'src/mentor/components/MentorClinicBookingsPage/component/BookingDateNavigator'
import BookingFilterStrip from 'src/mentor/components/MentorClinicBookingsPage/component/BookingFilterStrip'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import { Button, Dialog, Box, Typography, IconButton, Tooltip, CircularProgress, Chip } from '@mui/material'
import { useState, useEffect, useContext, useMemo, useCallback, useRef, memo } from 'react'
import { useNavigate } from 'react-router'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import LoginIcon from '@mui/icons-material/Login'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import CheckInDialogComponent from 'src/clinic/components/CheckIn'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import { toast } from 'react-toastify'
import { getMPDataWithParams } from 'src/mentor/services'
import { getCPDataWithParams } from 'src/clinic/services'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'

dayjs.extend(utc)
import { components } from 'react-select'
import { styled } from '@mui/material/styles'
import {
  CheckoutButtonComponent,
} from 'src/clinic/components/CheckIn/checkInComponents'
import { PastTherapyReportDialogPaper } from 'src/clinic/components/ClinicManageBookingsPage/receiptPrinting/styledComponents'
import BaseUITabsComponent from 'src/common/tabs/BaseUITabs'
import { useScreenSize } from 'src/customHooks/customHooks'
import { MENTOR_NEW_BOOKING_TABS } from 'src/mentor/components/MentorClinicBookingsPage/component/newBooking/newBookingConsts'
import NewBookingPopup from 'src/mentor/components/MentorClinicBookingsPage/component/newBooking/NewBookingPopup'
import { MENTOR_CLINIC_BOOKING_TABS_LIST } from 'src/mentor/components/MentorClinicBookingsPage/util'
import CheckinRequestPromptDialog from 'src/mentor/components/CheckinRequestPromptDialog'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import {
  createEmrCheckInRequest,
  getCheckInRequestErrorMessage,
} from 'src/mentor/services/checkInRequestService'
import ClinicAssistantNameButton from 'src/clinic/components/ClinicAssistantProfileDrawer/ClinicAssistantNameButton'

export const ClinicSelectValueComp = (props) => {
  const item = localStorage.getItem('checkedInDetails')

  if (!item) {
    return <div></div>
  }

  const parsedItem = JSON.parse(item)
  return (
    <components.SingleValue {...props}>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <span style={{ display: 'flex', alignItems: 'center' }}>{props.children}</span>
        <span
          style={{ display: 'flex', alignItems: 'center', whiteSpace: 'break-spaces' }}
          className="fs-7"
        >
          {parsedItem?.roomName}
        </span>
      </div>
    </components.SingleValue>
  )
}

// Styled container for the modernized header.
// IMPORTANT: defined at module scope (NOT inside the component). Defining styled() inside the render
// creates a new component type every render, which makes React unmount/remount the whole header
// subtree on each render — combined with the 1s check-in poll that caused the header card to visibly
// "reload" every second.
const ModernHeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '8px 20px',
  gap: '16px',
  zIndex: 11,
  position: 'relative',
  margin: 0,
  marginTop: 0,
  marginBottom: 0,
  overflow: 'visible',
  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(249, 250, 251, 0.98) 100%)',
  backdropFilter: 'blur(20px) saturate(180%)',
  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
  borderBottom: '1px solid rgba(16, 185, 129, 0.12)',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.06)',
  [theme.breakpoints.down('md')]: {
    padding: '6px 16px',
    gap: '12px',
  },
}))

const ClinicInfoBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '8px 16px',
  backgroundColor: 'rgba(16, 185, 129, 0.06)',
  borderRadius: '10px',
  border: '1px solid rgba(16, 185, 129, 0.15)',
  [theme.breakpoints.down('sm')]: {
    padding: '6px 12px',
    gap: '8px',
  },
}))

/**
 * Self-contained header clock. Owns its own ticking state so its per-minute updates re-render ONLY
 * this small component, not the whole header (and never remount neighboring cards).
 */
const HeaderClock = memo(function HeaderClock() {
  const [now, setNow] = useState(() => dayjs())
  useEffect(() => {
    const id = setInterval(() => setNow(dayjs()), 30000)
    return () => clearInterval(id)
  }, [])
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        flexShrink: 0,
        padding: '6px 10px',
        borderRadius: '10px',
        border: '1px solid rgba(100, 116, 139, 0.18)',
        backgroundColor: 'rgba(100, 116, 139, 0.06)',
      }}
      title={now.format('dddd, D MMM YYYY')}
    >
      <AccessTimeIcon sx={{ fontSize: 15, color: 'rgba(100, 116, 139, 0.8)' }} />
      <Typography sx={{ fontSize: '12px', fontWeight: 700, color: 'rgba(71, 85, 105, 0.95)', whiteSpace: 'nowrap' }}>
        {now.format('h:mm A')}
      </Typography>
    </Box>
  )
})

function HeaderItems({
  clinicAssociationData,
  clinicId,
  clinicBrandId: _clinicBrandId,
  selectedTab,
  setSelectedTab,
  mentorProfileData,
  refreshData,
  hideRefreshButton = false,
  hideClinicDetails = false,
  hideCheckinDetails = false,
  hideTabs = false,
  mentorsList = [],
  searchPatientApiSignature: _searchPatientApiSignature = null,
  appointmentTypesList = [],
  createPatientApiSignature: _createPatientApiSignature = null,
  excludeTabs = [],
  patientHistoryGetApiSignature,
  hidePatientHistoryRelatedTabs = false,
  hidePatientAddButton: _hidePatientAddButton = false,
  hideCheckInButton: _hideCheckInButton = false,
  customExitButton = null,
  hideSendToDoctorButton = false,
  onOpenPatientSearch,
  onOpenNewBooking,
  showNewBookingButton = false,
  children, // Keep for backward compatibility, but prefer practitionerButtonProps
  practitionerButtonProps: _practitionerButtonProps, // New: primitive props for practitioner button
  onOpenPractitionerDrawer: _onOpenPractitionerDrawer, // New: handler for opening practitioner drawer
  onAssistantNameClick, // Handler for clicking clinic assistant name (optional)
  isClinicAssistantFlow = false, // Flag to indicate clinic assistant flow
  pageHeaderActionNode = null,
  // Opt-in: render a "Search / Add Patient" button in this (top) header. Used by the mentor clinic
  // bookings page after moving the search action out of the calendar floating header.
  showPatientSearchInHeader = false,
  // Opt-in: render a hamburger (mobile) that opens the page's left-menu drawer.
  onOpenNav,
  // Opt-in: render a clinic card (brand + location + live time) with the Checkout/Exit button
  // integrated, at the far right of the desktop header (mentor clinic bookings page).
  showClinicExitCard = false,
  // Opt-in: render the sleek day navigator (date + calendar dropdown) centred in the desktop header.
  // The caller decides (by viewport width) whether this or the calendar floating header owns it.
  showDateNavigator = false,
  dateNavigatorClinicSchedules,
  // Opt-in: render the slim filter-pill strip as a second row of this (fixed) header. The pills'
  // data is mirrored from the calendar over an event bridge (see BookingFilterStrip).
  showFilterStrip = false,
  // Opt-in: when provided, the "Check In" button / "Peeking Privately" chip open the
  // caller's check-in screen (ClinicLocationCheckInGate) instead of the legacy
  // room-status dialog flow (handleCheckInPrompt). Used by MentorClinicBookingsPage.
  onOpenCheckInGate = null,
}) {
  const navigate = useNavigate()
  const mentorProfileContext = useContext(MentorProfileContext)
  const contextMentorProfileData = mentorProfileContext?.mentorProfileData
  const mentorProfile = mentorProfileData || contextMentorProfileData
  const [selectedPatient, setSelectedPatient] = useState()
  const [refreshing, setRefreshing] = useState(false)
  const [checkedInTime, setCheckedInTime] = useState(null)
  const [checkInRefreshedTime, setCheckInRefreshedTime] = useState(null)
  const [isCheckedIn, setIsCheckedIn] = useState(false)
  const [isPeekingIn, setIsPeekingIn] = useState(false)
  const [peekedRoomData, setPeekedRoomData] = useState(null)
  const [checkInModalOpen, setCheckInModalOpen] = useState(false)
  const [dialogRoomData, setDialogRoomData] = useState(null) // Snapshot for dialog to prevent blinking
  const [requestCheckoutDialogOpen, setRequestCheckoutDialogOpen] = useState(false)
  const [requestCheckoutRoomData, setRequestCheckoutRoomData] = useState(null)
  const [requestCheckoutLoading, setRequestCheckoutLoading] = useState(false)
  const checkInModalOpenRef = useRef(false) // Ref to track dialog state without causing re-renders
  const screenSize = useScreenSize()
  const allRoles = useMemo(() => getAllRolesInBoolean(), [])

  const currentClinic = clinicAssociationData?.find((cln) => cln?.id === clinicId)

  // Keep ref in sync with state
  useEffect(() => {
    checkInModalOpenRef.current = checkInModalOpen
  }, [checkInModalOpen])

  // Close dialog and clear snapshot - memoized to prevent re-renders
  const handleCloseCheckInDialog = useCallback(() => {
    setCheckInModalOpen(false)
    // Clear dialog room data after a short delay to prevent flicker
    setTimeout(() => {
      setDialogRoomData(null)
    }, 300)
  }, [])

  const openRequestCheckoutDialog = useCallback((roomData) => {
    setRequestCheckoutRoomData(roomData)
    setRequestCheckoutDialogOpen(true)
  }, [])

  // Auto-close check-in dialog after 30 seconds of inactivity
  useEffect(() => {
    if (!checkInModalOpen) {
      return
    }

    const autoCloseTimer = setTimeout(() => {
      handleCloseCheckInDialog()
      // Silently close without toast notification
    }, 30000) // 30 seconds

    return () => {
      clearTimeout(autoCloseTimer)
    }
  }, [checkInModalOpen, handleCloseCheckInDialog])

  // Get check-in status and peek status from localStorage
  useEffect(() => {
    const getCheckInStatus = () => {
      const checkedInDetails = localStorage.getItem('checkedInDetails')
      const peekedInDetails = localStorage.getItem('peekedInRoomDetails')
      
      let checkInTime = null
      let refreshedTime = null
      let checkedIn = false
      let peeking = false
      let roomData = null
      let parsedPeekedInDetails = null

      if (peekedInDetails) {
        try {
          parsedPeekedInDetails = JSON.parse(peekedInDetails)
        } catch (e) {
          console.error('Error parsing peekedInRoomDetails:', e)
        }
      }
      
      if (checkedInDetails) {
        try {
          const parsed = JSON.parse(checkedInDetails)
          const checkInCreatedTime =
            parsed?.checkInCreatedTime ||
            parsed?.mentorLastCheckedIn ||
            parsedPeekedInDetails?.mentorLastCheckedIn ||
            parsed?.createdTime
          if (checkInCreatedTime) {
            checkInTime = dayjs.utc(checkInCreatedTime).local()
            checkedIn = true
          }

          const lastCheckInRefreshTime =
            parsed?.lastCheckInRefreshCreatedTime ||
            parsed?.mentorLastCheckinRefreshed ||
            parsedPeekedInDetails?.mentorLastCheckinRefreshed
          if (lastCheckInRefreshTime) {
            refreshedTime = dayjs.utc(lastCheckInRefreshTime).local()
          }
        } catch (e) {
          console.error('Error parsing checkedInDetails:', e)
        }
      }
      
      if (parsedPeekedInDetails && !checkedIn) {
        if (parsedPeekedInDetails?.id) {
          peeking = true
          roomData = parsedPeekedInDetails
        }
      }
      
      return { checkInTime, refreshedTime, checkedIn, peeking, roomData }
    }

    const status = getCheckInStatus()
    setCheckedInTime(status.checkInTime)
    setCheckInRefreshedTime(status.refreshedTime)
    setIsCheckedIn(status.checkedIn)
    setIsPeekingIn(status.peeking)
    // Only update peekedRoomData if dialog is not open to prevent blinking
    if (!checkInModalOpenRef.current) {
      setPeekedRoomData(status.roomData)
    }
    
    // Update every second to keep status current
    const interval = setInterval(() => {
      const newStatus = getCheckInStatus()
      setCheckedInTime(newStatus.checkInTime)
      setCheckInRefreshedTime(newStatus.refreshedTime)
      setIsCheckedIn(newStatus.checkedIn)
      setIsPeekingIn(newStatus.peeking)
      // Only update peekedRoomData if dialog is not open to prevent blinking
      if (!checkInModalOpenRef.current) {
        setPeekedRoomData(newStatus.roomData)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  // Handle check-in prompt (from "Check In" button or "Peeking Privately" chip)
  const handleCheckInPrompt = async () => {
    if (!peekedRoomData) {
      toast.error('No room information available. Please select a room first.')
      return
    }

    if (!currentClinic?.id) {
      toast.error('No clinic information available.')
      return
    }

    const isClinicAssistant = getAllRolesInBoolean().isClinicAssistant

    try {
      let response
      let rooms = []
      let currentRoom = null

      if (isClinicAssistant) {
        // Use Clinic Assistant API
        response = await getCPDataWithParams(
          `/api/secure/clinic-assistant/associated/chamber-room/list`,
          {
            clinicId: currentClinic.id,
          },
        )

        if (response?.status === 200) {
          rooms = Array.isArray(response?.data) ? response?.data : []
          currentRoom = rooms.find((room) => room?.id === peekedRoomData?.id)
        }
      } else {
        // Use Mentor/Teacher API
        response = await getMPDataWithParams(`/api/secure/teacher/associated-chamber-rooms`, {
          clinicLocationId: currentClinic.id,
        })

        if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
          rooms = response?.data?.data?.['defined-room-access'] || []
          currentRoom = rooms.find((room) => room?.id === peekedRoomData?.id)
        }
      }

      if (response?.status === 200) {
        if (!currentRoom) {
          toast.error('Room not found. Please try again.')
          return
        }

        // For clinic assistants, room status checking might not be available in the same way
        // For mentors, check if room is occupied by someone else using fresh data
        if (!isClinicAssistant) {
          const roomOccupied = currentRoom?.occupied
          const occupiedByThisMentor = 
            roomOccupied && currentRoom?.checkedInMentorId === mentorProfile?.id
          if (roomOccupied && !occupiedByThisMentor) {
            const updatedRoomData = {
              ...peekedRoomData,
              occupied: currentRoom.occupied,
              checkedInMentorId: currentRoom.checkedInMentorId,
              checkedInMentorName: currentRoom.checkedInMentorName,
            }
            localStorage.setItem('peekedInRoomDetails', JSON.stringify(updatedRoomData))
            setPeekedRoomData(updatedRoomData)
            openRequestCheckoutDialog(updatedRoomData)
            return
          }

          // Update localStorage with fresh room data before proceeding
          const updatedRoomData = {
            ...peekedRoomData,
            occupied: currentRoom.occupied,
            checkedInMentorId: currentRoom.checkedInMentorId,
            checkedInMentorName: currentRoom.checkedInMentorName,
          }
          localStorage.setItem('peekedInRoomDetails', JSON.stringify(updatedRoomData))
          setPeekedRoomData(updatedRoomData)
          setDialogRoomData(JSON.parse(JSON.stringify(updatedRoomData))) // Deep copy
        } else {
          // For clinic assistants, use the room data as-is
          const updatedRoomData = {
            ...peekedRoomData,
            ...currentRoom,
          }
          localStorage.setItem('peekedInRoomDetails', JSON.stringify(updatedRoomData))
          setPeekedRoomData(updatedRoomData)
          setDialogRoomData(JSON.parse(JSON.stringify(updatedRoomData))) // Deep copy
        }

        // Store a snapshot of updated room data for the dialog to prevent re-rendering issues
        setCheckInModalOpen(true)
      } else {
        // If API call fails, fall back to using cached data but show a warning
        toast.warning('Could not refresh room status. Using cached information.', { autoClose: 3000 })
        
        // For clinic assistants, skip room occupancy check
        // For mentors, check if room is occupied by someone else using cached data
        if (!isClinicAssistant) {
          const roomOccupied = peekedRoomData?.occupied
          const occupiedByThisMentor = 
            roomOccupied && peekedRoomData?.checkedInMentorId === mentorProfile?.id
          if (roomOccupied && !occupiedByThisMentor) {
            openRequestCheckoutDialog(peekedRoomData)
            return
          }
        }

        // Store a snapshot of room data for the dialog to prevent re-rendering issues
        setDialogRoomData(JSON.parse(JSON.stringify(peekedRoomData))) // Deep copy
        setCheckInModalOpen(true)
      }
    } catch (error) {
      console.error('Error fetching room status:', error)
      toast.error('Failed to refresh room status. Please try again.', { autoClose: 3000 })
      
      // For clinic assistants, skip room occupancy check
      // For mentors, fall back to using cached data
      if (!isClinicAssistant) {
        const roomOccupied = peekedRoomData?.occupied
        const occupiedByThisMentor = 
          roomOccupied && peekedRoomData?.checkedInMentorId === mentorProfile?.id
        if (roomOccupied && !occupiedByThisMentor) {
          openRequestCheckoutDialog(peekedRoomData)
          return
        }
      }

      // Store a snapshot of room data for the dialog to prevent re-rendering issues
      setDialogRoomData(JSON.parse(JSON.stringify(peekedRoomData))) // Deep copy
      setCheckInModalOpen(true)
    }
  }

  // Memoize dialog props to prevent unnecessary re-renders
  const checkInDialogProps = useMemo(() => {
    if (!checkInModalOpen || !currentClinic || !dialogRoomData) {
      return null
    }
    return {
      closeFunction: handleCloseCheckInDialog,
      roomData: dialogRoomData,
      clinicLocationData: currentClinic,
      onChoosingInPerson: async () => {
        handleCloseCheckInDialog()
        // Refresh will happen automatically via the useEffect
      },
      onChoosingRemote: () => {
        handleCloseCheckInDialog()
        navigate(
          `/mentor-home/join-chamber-room/${currentClinic?.clinicBrandId}/${currentClinic?.id}`,
        )
      },
    }
  }, [checkInModalOpen, currentClinic, dialogRoomData, handleCloseCheckInDialog, navigate])

  const handleSubmitCheckoutRequest = async (reason) => {
    if (!requestCheckoutRoomData?.id) {
      toast.error('No occupied room found for this request.')
      return
    }

    setRequestCheckoutLoading(true)

    try {
      const { request, response } = await createEmrCheckInRequest({
        chamberRoomId: requestCheckoutRoomData.id,
        reason,
      })

      if (request) {
        setRequestCheckoutDialogOpen(false)
        setRequestCheckoutRoomData(null)
        toast.success(
          `Checkout request sent to ${
            requestCheckoutRoomData?.checkedInMentorName || 'the checked-in mentor'
          }.`,
        )
        return
      }

      toast.error(
        getCheckInRequestErrorMessage(response, 'Could not send the checkout request.'),
      )
    } catch (error) {
      console.error('Error sending checkout request:', error)
      toast.error('Could not send the checkout request.')
    } finally {
      setRequestCheckoutLoading(false)
    }
  }

  const handleClick = (e) => {
    setSelectedTab(e)
  }

  function closeDialog() {
    setSelectedPatient(null)
  }

  async function refreshBookedSlots() {
    setRefreshing(true)
    await refreshData()
    setRefreshing(false)
  }

  const isMobile = screenSize.width < 768
  const availableTabs = useMemo(() => {
    return MENTOR_CLINIC_BOOKING_TABS_LIST?.filter((tab) => {
      if (excludeTabs?.includes(tab?.id)) {
        return false
      }
      if (allRoles.isDoctor) {
        return tab?.roleType?.includes('ROLE_DOCTOR')
      }
      if (allRoles.isClinicAssistant) {
        return tab?.roleType?.includes('ROLE_CLINIC_ASSISTANT')
      }
      return tab?.roleType?.includes('ROLE_THERAPIST')
    })
  }, [allRoles.isClinicAssistant, allRoles.isDoctor, excludeTabs])
  const mobileSearchLabel = screenSize.width < 430 ? 'Search' : 'Search / Add'
  const showCompactMobileSearchButton = screenSize.width < 400
  const showMobileClinicBrand = Boolean(currentClinic?.clinicBrandName && screenSize.width >= 380)
  const showMobileSpecialization = Boolean(mentorProfileData?.specialization && screenSize.width >= 420)
  const showMobileTabs = !hideTabs && availableTabs?.length > 0
  // On mobile, when the slim filter-pill strip is present, relocate the nav (hamburger) and patient
  // search into that strip so they share a single toolbar row instead of occupying a near-empty band
  // of their own. This keeps the top chrome slimmer and more visually consistent.
  const relocateNavToFilterStrip =
    isMobile && showFilterStrip && Boolean(onOpenNav || onOpenPatientSearch)
  const showMobileActionRow =
    showMobileTabs ||
    !hideRefreshButton ||
    Boolean(pageHeaderActionNode) ||
    (!relocateNavToFilterStrip &&
      (Boolean(onOpenPatientSearch) ||
        Boolean(onOpenNav) ||
        (showNewBookingButton && Boolean(onOpenNewBooking))))


  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
    <ModernHeaderContainer sx={showFilterStrip ? { borderBottom: 'none' } : undefined}>
      {/* Desktop: Full Layout */}
      {!isMobile && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0, overflow: 'visible' }}>
            {/* Mentor Name, Check-in Time, and Status - Structured Together */}
            {mentorProfileData && (
              <ClinicInfoBox>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <ClinicAssistantNameButton
                      name={mentorProfileData?.name || mentorProfileData?.firstName}
                      onClick={isClinicAssistantFlow && onAssistantNameClick ? onAssistantNameClick : undefined}
                      variant="subtitle2"
                    />
                    {isPeekingIn && (
                      <Chip
                        icon={<VisibilityOffIcon sx={{ fontSize: 14 }} />}
                        label="Peeking Privately"
                        size="small"
                        onClick={onOpenCheckInGate || handleCheckInPrompt}
                        sx={{
                          height: '20px',
                          fontSize: '10px',
                          fontWeight: 600,
                          backgroundColor: 'rgba(116, 80, 170, 0.15)',
                          color: '#7450aa',
                          border: '1px solid rgba(116, 80, 170, 0.3)',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(116, 80, 170, 0.25)',
                            borderColor: 'rgba(116, 80, 170, 0.5)',
                          },
                        }}
                      />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    {mentorProfileData?.specialization && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'rgba(100, 116, 139, 0.75)',
                          fontSize: '11px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={mentorProfileData?.specialization}
                      >
                        {mentorProfileData?.specialization}
                      </Typography>
                    )}
                    {checkedInTime && (
                      <>
                        {mentorProfileData?.specialization && (
                          <Typography variant="caption" sx={{ color: 'rgba(100, 116, 139, 0.5)', fontSize: '11px' }}>
                            •
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AccessTimeIcon sx={{ fontSize: 12, color: 'rgba(16, 185, 129, 0.8)' }} />
                          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'rgba(16, 185, 129, 0.9)',
                                fontSize: '11px',
                                fontWeight: 600,
                              }}
                            >
                              Checked in: {checkedInTime.format('h:mm A')}
                            </Typography>
                            {checkInRefreshedTime && (
                              <Typography
                                variant="caption"
                                title="Last check-in refresh"
                                sx={{
                                  color: 'rgba(100, 116, 139, 0.52)',
                                  fontSize: '9.5px',
                                  fontWeight: 500,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                refresh {checkInRefreshedTime.format('h:mm A')}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </>
                    )}
                    {!isCheckedIn && !isPeekingIn && (
                      <>
                        {mentorProfileData?.specialization && (
                          <Typography variant="caption" sx={{ color: 'rgba(100, 116, 139, 0.5)', fontSize: '11px' }}>
                            •
                          </Typography>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<LoginIcon sx={{ fontSize: 14 }} />}
                          onClick={onOpenCheckInGate || handleCheckInPrompt}
                          sx={{
                            fontSize: '11px',
                            fontWeight: 600,
                            padding: '2px 8px',
                            height: '22px',
                            borderColor: 'rgba(16, 185, 129, 0.4)',
                            color: 'rgba(16, 185, 129, 0.9)',
                            '&:hover': {
                              borderColor: 'rgba(16, 185, 129, 0.6)',
                              backgroundColor: 'rgba(16, 185, 129, 0.08)',
                            },
                          }}
                        >
                          Check In
                        </Button>
                      </>
                    )}
                  </Box>
                </Box>
              </ClinicInfoBox>
            )}

            {/* Tabs */}
            {!hideTabs && (
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: { xs: 0.5, md: 1 },
                  overflowX: 'auto',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                }}
              >
                <BaseUITabsComponent
                  onChange={handleClick}
                  value={selectedTab}
                  containerStyle={{ marginLeft: 0, width: '100%' }}
                  containerClassName="fs-6 gap-1"
                  tabs={availableTabs}
                  getTabId={(tab) => tab?.id}
                  getTabLabel={(tab) => tab?.title}
                  getTabIcon={(tab) => tab?.icon}
                  alignContentInColumnForm={false}
                  hideTextInMobileMode={isMobile}
                  incicatorColor="emrSecondary"
                  variant="contained"
                  unselectedColor="rgba(71, 85, 105, 0.85)"
                  selectedColor="rgba(16, 185, 129, 0.95)"
                  hoverItemColor="rgba(16, 185, 129, 0.9)"
                />
              </Box>
            )}

            {/* Day navigator — centred in the space the tabs would occupy. Shown only when the page
                is wide enough; otherwise the calendar floating header keeps the date picker. */}
            {hideTabs && showDateNavigator && (
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <BookingDateNavigator clinicSchedules={dateNavigatorClinicSchedules} />
              </Box>
            )}
          </Box>

          {/* Right Side Actions - Desktop */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexShrink: 0,
            }}
          >
            {pageHeaderActionNode}

            {/* Patient search / add - opt-in (moved here from the calendar floating header) */}
            {showPatientSearchInHeader && onOpenPatientSearch && (
              <Button
                onClick={onOpenPatientSearch}
                variant="outlined"
                startIcon={<SearchIcon sx={{ fontSize: 18 }} />}
                sx={{
                  flexShrink: 0,
                  color: '#6779e3',
                  borderColor: 'rgba(103, 121, 227, 0.28)',
                  backgroundColor: 'rgba(103, 121, 227, 0.08)',
                  fontSize: '13px',
                  fontWeight: 700,
                  padding: '6px 14px',
                  borderRadius: '10px',
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  '& .MuiButton-startIcon': {
                    marginRight: '6px',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(103, 121, 227, 0.14)',
                    borderColor: 'rgba(103, 121, 227, 0.45)',
                  },
                }}
              >
                Search / Add Patient
              </Button>
            )}

            {/* New Appointment - Appointments tab only (moved here from the calendar floating header).
                Gated on its own flag so it stays even on tabs where the search button is hidden. */}
            {showNewBookingButton && onOpenNewBooking && (
              <Button
                onClick={onOpenNewBooking}
                variant="contained"
                startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                sx={{
                  flexShrink: 0,
                  backgroundColor: '#10b981',
                  color: 'white',
                  fontSize: '13px',
                  fontWeight: 700,
                  padding: '6px 14px',
                  borderRadius: '10px',
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 8px rgba(16, 185, 129, 0.25)',
                  '& .MuiButton-startIcon': {
                    marginRight: '6px',
                  },
                  '&:hover': {
                    backgroundColor: '#059669',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.35)',
                  },
                }}
              >
                New Appointment
              </Button>
            )}

            {/* Practitioner Button removed from main header - only shown in floating header */}
            {children}

            {/* Refresh Button */}
            {!hideRefreshButton && (
              <Tooltip title="Refresh Calendar" arrow>
                <span>
                  <IconButton
                    onClick={refreshBookedSlots}
                    disabled={refreshing}
                    sx={{
                      color: 'rgba(16, 185, 129, 0.9)',
                      backgroundColor: 'rgba(16, 185, 129, 0.08)',
                      border: '1px solid rgba(16, 185, 129, 0.2)',
                      padding: '8px',
                      '&:hover': {
                        backgroundColor: 'rgba(16, 185, 129, 0.12)',
                        borderColor: 'rgba(16, 185, 129, 0.3)',
                        transform: 'scale(1.05)',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                        borderColor: 'rgba(0, 0, 0, 0.08)',
                        color: 'rgba(0, 0, 0, 0.26)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {refreshing ? (
                      <CircularProgress size={18} sx={{ color: 'rgba(16, 185, 129, 0.7)' }} />
                    ) : (
                      <RefreshIcon sx={{ fontSize: 20 }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}

            {/* Standalone header clock (isolated re-renders) — shown alongside the clinic card */}
            {showClinicExitCard && <HeaderClock />}

            {/* Checkout / Exit - either the unified clinic card (mentor) or the standalone button */}
            {customExitButton ? (
              customExitButton
            ) : showClinicExitCard ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'stretch',
                  flexShrink: 0,
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: '1px solid rgba(16, 185, 129, 0.18)',
                  backgroundColor: 'rgba(16, 185, 129, 0.05)',
                  boxShadow: '0 1px 3px rgba(16, 185, 129, 0.08)',
                }}
              >
                {/* Clinic brand / location / live time */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    gap: 0.25,
                    padding: '6px 14px',
                    minWidth: 0,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                    <LocalHospitalIcon sx={{ fontSize: 14, color: 'rgba(16, 185, 129, 0.9)', flexShrink: 0 }} />
                    <Typography
                      sx={{
                        fontSize: '12px',
                        fontWeight: 700,
                        color: 'rgba(15, 23, 42, 0.9)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: 200,
                      }}
                      title={currentClinic?.clinicBrandName}
                    >
                      {currentClinic?.clinicBrandName || 'Clinic'}
                    </Typography>
                  </Box>
                  {currentClinic?.title && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                      <LocationOnIcon sx={{ fontSize: 12, color: 'rgba(100, 116, 139, 0.7)', flexShrink: 0 }} />
                      <Typography
                        sx={{
                          fontSize: '11px',
                          color: 'rgba(100, 116, 139, 0.85)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: 150,
                        }}
                        title={currentClinic?.title}
                      >
                        {currentClinic?.title}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Exit / Checkout - integrated into the card */}
                {!hideCheckinDetails && (
                  <Box sx={{ display: 'flex', alignItems: 'stretch', borderLeft: '1px solid rgba(16, 185, 129, 0.18)' }}>
                    <CheckoutButtonComponent
                      text="Checkout"
                      buttonColor="error"
                      styleProp={{
                        flexDirection: 'row',
                        fontSize: '13px',
                        fontWeight: 700,
                        padding: '8px 18px',
                        height: '100%',
                        minWidth: '104px',
                        borderRadius: 0,
                        backgroundColor: '#ef4444',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#dc2626',
                        },
                      }}
                      showExitButtonIfNotCheckedIn={true}
                    />
                  </Box>
                )}
              </Box>
            ) : (
              <>
                {!hideCheckinDetails && (
                  <Box sx={{ flexShrink: 0 }}>
                    <CheckoutButtonComponent
                      text="Checkout"
                      buttonColor="error"
                      styleProp={{
                        flexDirection: 'row',
                        fontSize: '14px',
                        fontWeight: 700,
                        padding: '8px 20px',
                        height: 'auto',
                        minWidth: '110px',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#dc2626',
                          boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)',
                        },
                        boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
                      }}
                      showExitButtonIfNotCheckedIn={true}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        </>
      )}

      {/* Mobile: Simplified Layout - Mentor Info + Checkout + Search */}
      {isMobile && (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 0.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'stretch', width: '100%', gap: 1 }}>
            {mentorProfileData && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.25,
                  padding: '6px 10px',
                  backgroundColor: isPeekingIn
                    ? 'rgba(116, 80, 170, 0.08)'
                    : 'rgba(16, 185, 129, 0.06)',
                  borderRadius: '10px',
                  border: isPeekingIn
                    ? '1px solid rgba(116, 80, 170, 0.2)'
                    : '1px solid rgba(16, 185, 129, 0.15)',
                  minWidth: 0,
                  flex: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                  <ClinicAssistantNameButton
                    name={mentorProfileData?.name || mentorProfileData?.firstName}
                    onClick={isClinicAssistantFlow && onAssistantNameClick ? onAssistantNameClick : undefined}
                    variant="caption"
                    sx={{
                      fontSize: '12px',
                    }}
                  />
                  {isPeekingIn && (
                    <Chip
                      icon={<VisibilityOffIcon sx={{ fontSize: 10 }} />}
                      label="Peeking"
                      size="small"
                      onClick={onOpenCheckInGate || handleCheckInPrompt}
                      sx={{
                        height: '18px',
                        fontSize: '9px',
                        fontWeight: 600,
                        backgroundColor: 'rgba(116, 80, 170, 0.15)',
                        color: '#7450aa',
                        border: '1px solid rgba(116, 80, 170, 0.3)',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'rgba(116, 80, 170, 0.25)',
                          borderColor: 'rgba(116, 80, 170, 0.5)',
                        },
                      }}
                    />
                  )}
                </Box>
                {!hideClinicDetails && currentClinic?.title && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                    <LocationOnIcon
                      sx={{
                        fontSize: 11,
                        color: 'rgba(15, 118, 110, 0.72)',
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(15, 23, 42, 0.84)',
                        fontSize: '10px',
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={currentClinic?.title}
                    >
                      {currentClinic?.title}
                    </Typography>
                    {showMobileClinicBrand && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'rgba(100, 116, 139, 0.76)',
                          fontSize: '10px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0,
                        }}
                        title={currentClinic?.clinicBrandName}
                      >
                        {`• ${currentClinic?.clinicBrandName}`}
                      </Typography>
                    )}
                  </Box>
                )}
                {/* Check-in time is intentionally omitted from the mobile card (it's noise on a
                    phone); the specialization line stays when there's room. */}
                {showMobileSpecialization && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'rgba(100, 116, 139, 0.78)',
                      fontSize: '10px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%',
                    }}
                    title={mentorProfileData?.specialization}
                  >
                    {mentorProfileData?.specialization}
                  </Typography>
                )}
                {!isCheckedIn && !isPeekingIn && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<LoginIcon sx={{ fontSize: 12 }} />}
                    onClick={onOpenCheckInGate || handleCheckInPrompt}
                    sx={{
                      alignSelf: 'flex-start',
                      fontSize: '10px',
                      fontWeight: 600,
                      padding: '2px 6px',
                      height: '20px',
                      borderColor: 'rgba(16, 185, 129, 0.4)',
                      color: 'rgba(16, 185, 129, 0.9)',
                      mt: 0.25,
                      '&:hover': {
                        borderColor: 'rgba(16, 185, 129, 0.6)',
                        backgroundColor: 'rgba(16, 185, 129, 0.08)',
                      },
                    }}
                  >
                    Check In
                  </Button>
                )}
              </Box>
            )}

            {customExitButton ? (
              customExitButton
            ) : (
              <>
                {!hideCheckinDetails && (
                  <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'stretch' }}>
                    <CheckoutButtonComponent
                      text="Checkout"
                      buttonColor="error"
                      styleProp={{
                        flexDirection: 'row',
                        fontSize: '12px',
                        fontWeight: 700,
                        padding: '8px 14px',
                        height: '100%',
                        minWidth: '96px',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#dc2626',
                          boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)',
                        },
                        boxShadow: '0 2px 6px rgba(239, 68, 68, 0.3)',
                      }}
                      showExitButtonIfNotCheckedIn={true}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>

          {showMobileActionRow && (
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
              {/* Hamburger - opens the page left-menu drawer (opt-in).
                  Suppressed here when it has been relocated into the filter strip (see below). */}
              {onOpenNav && !relocateNavToFilterStrip && (
                <Tooltip title="Menu" arrow>
                  <IconButton
                    onClick={onOpenNav}
                    sx={{
                      flexShrink: 0,
                      color: 'rgba(16, 185, 129, 0.9)',
                      border: '1px solid rgba(16, 185, 129, 0.2)',
                      backgroundColor: 'rgba(16, 185, 129, 0.08)',
                      padding: '10px',
                      borderRadius: '10px',
                      '&:hover': {
                        backgroundColor: 'rgba(16, 185, 129, 0.12)',
                        borderColor: 'rgba(16, 185, 129, 0.3)',
                      },
                    }}
                  >
                    <MenuIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              )}

              {onOpenPatientSearch && !relocateNavToFilterStrip && (
                showCompactMobileSearchButton ? (
                  <Tooltip title="Search / Add Patient" arrow>
                    <IconButton
                      onClick={onOpenPatientSearch}
                      sx={{
                        flexShrink: 0,
                        color: '#6779e3',
                        border: '1px solid rgba(103, 121, 227, 0.24)',
                        backgroundColor: 'rgba(103, 121, 227, 0.08)',
                        padding: '10px',
                        borderRadius: '10px',
                        '&:hover': {
                          backgroundColor: 'rgba(103, 121, 227, 0.14)',
                          borderColor: 'rgba(103, 121, 227, 0.45)',
                        },
                      }}
                    >
                      <SearchIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Button
                    onClick={onOpenPatientSearch}
                    variant="outlined"
                    startIcon={<SearchIcon sx={{ fontSize: 18 }} />}
                    sx={{
                      flexShrink: 0,
                      minWidth: screenSize.width < 430 ? '104px' : '118px',
                      justifyContent: 'center',
                      color: '#6779e3',
                      borderColor: 'rgba(103, 121, 227, 0.28)',
                      backgroundColor: 'rgba(103, 121, 227, 0.08)',
                      fontSize: '12px',
                      fontWeight: 700,
                      padding: '8px 10px',
                      borderRadius: '10px',
                      textTransform: 'none',
                      whiteSpace: 'nowrap',
                      '& .MuiButton-startIcon': {
                        marginRight: '4px',
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(103, 121, 227, 0.14)',
                        borderColor: 'rgba(103, 121, 227, 0.45)',
                      },
                    }}
                  >
                    {mobileSearchLabel}
                  </Button>
                )
              )}

              {/* New patient booking - Appointments tab only.
                  Suppressed here when relocated into the filter strip (see below). */}
              {showNewBookingButton && onOpenNewBooking && !relocateNavToFilterStrip && (
                <NewBookingHeaderButton
                  onClick={onOpenNewBooking}
                  compact={showCompactMobileSearchButton}
                />
              )}

              {showMobileTabs && (
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    overflowX: 'auto',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': {
                      display: 'none',
                    },
                  }}
                >
                  <BaseUITabsComponent
                    onChange={handleClick}
                    value={selectedTab}
                    tabs={availableTabs}
                    getTabId={(tab) => tab?.id}
                    getTabLabel={(tab) => tab?.title}
                    getTabIcon={(tab) => tab?.icon}
                    alignContentInColumnForm={false}
                    hideTextInMobileMode={false}
                    incicatorColor="emrSecondary"
                    variant="contained"
                    unselectedColor="rgba(71, 85, 105, 0.82)"
                    selectedColor="rgba(16, 185, 129, 0.95)"
                    hoverItemColor="rgba(16, 185, 129, 0.9)"
                  />
                </Box>
              )}

              {!hideRefreshButton && (
                <Tooltip title="Refresh Calendar" arrow>
                  <span>
                    <IconButton
                      onClick={refreshBookedSlots}
                      disabled={refreshing}
                      sx={{
                        color: 'rgba(16, 185, 129, 0.9)',
                        backgroundColor: 'rgba(16, 185, 129, 0.08)',
                        border: '1px solid rgba(16, 185, 129, 0.2)',
                        padding: '10px',
                        '&:hover': {
                          backgroundColor: 'rgba(16, 185, 129, 0.12)',
                          borderColor: 'rgba(16, 185, 129, 0.3)',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: 'rgba(0, 0, 0, 0.02)',
                          borderColor: 'rgba(0, 0, 0, 0.08)',
                          color: 'rgba(0, 0, 0, 0.26)',
                        },
                      }}
                    >
                      {refreshing ? (
                        <CircularProgress size={18} sx={{ color: 'rgba(16, 185, 129, 0.7)' }} />
                      ) : (
                        <RefreshIcon sx={{ fontSize: 20 }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              {pageHeaderActionNode}
            </Box>
          )}
        </Box>
      )}
      <Dialog
        open={!!selectedPatient}
        onClose={closeDialog}
        PaperComponent={PastTherapyReportDialogPaper}
      >
        <NewBookingPopup
          selectedPatient={selectedPatient}
          refreshData={refreshBookedSlots}
          mentorsList={mentorsList}
          appointmentTypesList={appointmentTypesList}
          closeDialog={closeDialog}
          patientHistoryGetApiSignature={patientHistoryGetApiSignature}
          hideSendToDoctorButton={hideSendToDoctorButton}
          filterOutTabs={
            hidePatientHistoryRelatedTabs
              ? [
                  MENTOR_NEW_BOOKING_TABS?.patientHistory?.id,
                  MENTOR_NEW_BOOKING_TABS?.patientReports?.id,
                ]
              : []
          }
        />
      </Dialog>

      {/* Check-in Dialog */}
      {checkInDialogProps && (
        <CheckInDialogComponent
          key={`checkin-dialog-${dialogRoomData?.id || 'default'}`}
          {...checkInDialogProps}
        />
      )}

      <CheckinRequestPromptDialog
        open={requestCheckoutDialogOpen}
        roomData={requestCheckoutRoomData}
        clinicLocationData={currentClinic}
        onClose={() => {
          if (!requestCheckoutLoading) {
            setRequestCheckoutDialogOpen(false)
            setRequestCheckoutRoomData(null)
          }
        }}
        onSubmit={handleSubmitCheckoutRequest}
        loading={requestCheckoutLoading}
      />
    </ModernHeaderContainer>

    {/* Slim filter-pill strip — a second row of the fixed header, lifted out of the calendar's
        floating header. Self-contained (mirrors calendar state over an event bridge).
        On mobile it also hosts the nav (hamburger), patient search and the "New" booking button,
        folded in from the action row above so the top chrome is a single tidy toolbar instead of
        two sparse bands. */}
    {showFilterStrip && (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          width: '100%',
          px: { xs: 1.25, md: 2 },
          py: '4px',
          background: 'linear-gradient(135deg, rgba(247, 250, 252, 0.96) 0%, rgba(255, 255, 255, 0.96) 100%)',
          borderBottom: '1px solid rgba(16, 185, 129, 0.12)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          zIndex: 11,
        }}
      >
        {relocateNavToFilterStrip && onOpenNav && (
          <Tooltip title="Menu" arrow>
            <IconButton
              onClick={onOpenNav}
              size="small"
              sx={{
                flexShrink: 0,
                color: 'rgba(16, 185, 129, 0.9)',
                border: '1px solid rgba(16, 185, 129, 0.2)',
                backgroundColor: 'rgba(16, 185, 129, 0.08)',
                padding: '6px',
                borderRadius: '9px',
                '&:hover': {
                  backgroundColor: 'rgba(16, 185, 129, 0.12)',
                  borderColor: 'rgba(16, 185, 129, 0.3)',
                },
              }}
            >
              <MenuIcon sx={{ fontSize: 19 }} />
            </IconButton>
          </Tooltip>
        )}

        {relocateNavToFilterStrip && onOpenPatientSearch && (
          <Tooltip title="Search / Add Patient" arrow>
            <IconButton
              onClick={onOpenPatientSearch}
              size="small"
              sx={{
                flexShrink: 0,
                color: '#6779e3',
                border: '1px solid rgba(103, 121, 227, 0.24)',
                backgroundColor: 'rgba(103, 121, 227, 0.08)',
                padding: '6px',
                borderRadius: '9px',
                '&:hover': {
                  backgroundColor: 'rgba(103, 121, 227, 0.14)',
                  borderColor: 'rgba(103, 121, 227, 0.45)',
                },
              }}
            >
              <SearchIcon sx={{ fontSize: 19 }} />
            </IconButton>
          </Tooltip>
        )}

        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center' }}>
          <BookingFilterStrip />
        </Box>

        {relocateNavToFilterStrip && showNewBookingButton && onOpenNewBooking && (
          <NewBookingHeaderButton onClick={onOpenNewBooking} compact />
        )}
      </Box>
    )}
    </Box>
  )
}

// Memoize HeaderItems to prevent unnecessary re-renders
// Use a custom comparison function that ignores children prop
// since children (PractitionerButton) is controlled separately
export default memo(HeaderItems, (prevProps, nextProps) => {
  // Compare all props except children
  return (
    prevProps.clinicAssociationData === nextProps.clinicAssociationData &&
    prevProps.clinicId === nextProps.clinicId &&
    prevProps.clinicBrandId === nextProps.clinicBrandId &&
    prevProps.selectedTab === nextProps.selectedTab &&
    prevProps.setSelectedTab === nextProps.setSelectedTab &&
    prevProps.mentorProfileData === nextProps.mentorProfileData &&
    prevProps.refreshData === nextProps.refreshData &&
    prevProps.hideRefreshButton === nextProps.hideRefreshButton &&
    prevProps.hideClinicDetails === nextProps.hideClinicDetails &&
    prevProps.hideCheckinDetails === nextProps.hideCheckinDetails &&
    prevProps.hideTabs === nextProps.hideTabs &&
    prevProps.mentorsList === nextProps.mentorsList &&
    prevProps.searchPatientApiSignature === nextProps.searchPatientApiSignature &&
    prevProps.appointmentTypesList === nextProps.appointmentTypesList &&
    prevProps.createPatientApiSignature === nextProps.createPatientApiSignature &&
    prevProps.excludeTabs === nextProps.excludeTabs &&
    prevProps.patientHistoryGetApiSignature === nextProps.patientHistoryGetApiSignature &&
    prevProps.hidePatientHistoryRelatedTabs === nextProps.hidePatientHistoryRelatedTabs &&
    prevProps.hidePatientAddButton === nextProps.hidePatientAddButton &&
    prevProps.hideCheckInButton === nextProps.hideCheckInButton &&
    prevProps.customExitButton === nextProps.customExitButton &&
    prevProps.hideSendToDoctorButton === nextProps.hideSendToDoctorButton &&
    prevProps.pageHeaderActionNode === nextProps.pageHeaderActionNode &&
    prevProps.showPatientSearchInHeader === nextProps.showPatientSearchInHeader &&
    prevProps.showClinicExitCard === nextProps.showClinicExitCard &&
    prevProps.showDateNavigator === nextProps.showDateNavigator &&
    prevProps.dateNavigatorClinicSchedules === nextProps.dateNavigatorClinicSchedules &&
    prevProps.showFilterStrip === nextProps.showFilterStrip &&
    prevProps.onOpenNav === nextProps.onOpenNav &&
    prevProps.onOpenPatientSearch === nextProps.onOpenPatientSearch &&
    prevProps.onOpenNewBooking === nextProps.onOpenNewBooking &&
    prevProps.showNewBookingButton === nextProps.showNewBookingButton &&
    // Compare practitionerButtonProps primitive values
    prevProps.practitionerButtonProps?.imageUrl === nextProps.practitionerButtonProps?.imageUrl &&
    prevProps.practitionerButtonProps?.managerId === nextProps.practitionerButtonProps?.managerId &&
    prevProps.practitionerButtonProps?.buttonText === nextProps.practitionerButtonProps?.buttonText &&
    prevProps.practitionerButtonProps?.isActive === nextProps.practitionerButtonProps?.isActive &&
    prevProps.onOpenPractitionerDrawer === nextProps.onOpenPractitionerDrawer
    // Note: We intentionally don't compare children prop for backward compatibility
    // If practitionerButtonProps is provided, it takes precedence over children
  )
})
